import { useRef, useEffect } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from './ChatMessage';
import { DateSeparator } from './DateSeparator';
import { isSameDay } from '../utils/date-utils';
import type { ChatThread, ChatMessage as ChatMessageType } from '../types';

interface MessageScrollAreaProps {
  thread: ChatThread;
  visibleMessages: Array<{ message: ChatMessageType; thread: ChatThread }>;
  threadMap: Map<string, ChatThread>;
  matterId?: number;
  onSendDraft?: (messageId: string) => void;
  onDeleteDraft?: (messageId: string) => void;
  onEditDraft?: (messageId: string) => void;
  editingDraftId?: string | null;
  institutionFaviconUrl?: string;
}

export function MessageScrollArea({
  thread: _thread,
  visibleMessages,
  threadMap: _threadMap,
  matterId,
  onSendDraft,
  onDeleteDraft,
  onEditDraft,
  editingDraftId,
  institutionFaviconUrl
}: MessageScrollAreaProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [visibleMessages]);

  // Render message content
  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    let lastThreadId: string | null = null;
    let lastDate: Date | null = null;

    visibleMessages.forEach((item, index) => {
      const { message, thread: messageThread } = item;

      // Date separator
      if (!lastDate || !isSameDay(lastDate, message.timestamp)) {
        elements.push(
          <DateSeparator
            key={`date-${message.timestamp.toISOString()}`}
            date={message.timestamp}
          />
        );
      }

      // Render message with thread indicators
      const showThreadIndicator =
        messageThread.id !== lastThreadId || index === 0;
      const isGrouped = !!(
        lastThreadId === messageThread.id &&
        lastDate &&
        isSameDay(lastDate, message.timestamp) &&
        index > 0 &&
        visibleMessages[index - 1].message.sender === message.sender
      );

      elements.push(
        <ChatMessage
          key={message.id}
          message={message}
          matterId={matterId}
          showTimestamp={!isGrouped || index === visibleMessages.length - 1}
          isGrouped={isGrouped}
          threadColor={messageThread.color || undefined}
          threadName={messageThread.subject}
          showThreadIndicator={showThreadIndicator}
          institutionFaviconUrl={institutionFaviconUrl}
          onSendDraft={onSendDraft}
          onDeleteDraft={onDeleteDraft}
          onEditDraft={onEditDraft}
          isBeingEdited={editingDraftId === message.id}
        />
      );

      lastThreadId = messageThread.id;
      lastDate = message.timestamp;
    });

    return elements;
  };

  return (
    <ScrollArea className="bg-card h-full w-full" ref={scrollAreaRef}>
      <div className="mx-auto max-w-4xl px-4 py-6">{renderContent()}</div>
    </ScrollArea>
  );
}
